import React, { useState, useEffect } from "react";
import axios from "../../api/axiosAuth";
import { useAuth } from "../../context/AuthContext";

const API_BASE = "http://localhost:5001/api/comportamiento";

export default function ModuloComportamiento() {
  const { token, user } = useAuth(); // obtenemos tenant_id y sede_id desde user

  const [tab, setTab] = useState("configuracion"); // configuracion | descriptores | evaluaciones
  const [grado, setGrado] = useState("");
  const [periodo, setPeriodo] = useState("");

  const [config, setConfig] = useState({ columnas: 5, mostrar_observaciones: 1, tipo_valoracion: "numerico", grado: "" });
  const [descriptores, setDescriptores] = useState([]);
  const [nuevoDescriptor, setNuevoDescriptor] = useState({ nombre_descriptor: "", tipo_valoracion: "numerico", posicion: 1, grado: "" });
  const [evaluaciones, setEvaluaciones] = useState([]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Helper: headers auth
  const authHeader = () => ({ headers: { Authorization: `Bearer ${token}` } });

  // ---------- Safe fetch wrapper ----------
  const safeFetch = async (fn) => {
    try {
      setLoading(true);
      await fn();
    } catch (err) {
      console.error(err);
      setError(err?.response?.data?.error || err.message || "Error inesperado");
      setTimeout(() => setError(""), 4000);
    } finally {
      setLoading(false);
    }
  };

  // ---------- Fetch configuración ----------
  const fetchConfig = async () => {
    await safeFetch(async () => {
      const res = await axios.get(`${API_BASE}/configuracion`, authHeader());
      const data = res?.data || null;
      if (!data) {
        setConfig({ columnas: 5, mostrar_observaciones: 1, tipo_valoracion: "numerico", grado: "" });
      } else {
        setConfig({
          columnas: data.columnas ?? 5,
          mostrar_observaciones: data.mostrar_observaciones ?? 1,
          tipo_valoracion: data.tipo_valoracion ?? "numerico",
          grado: data.grado ?? "",
        });
        if (data.grado) setGrado(data.grado);
      }
    });
  };

  // ---------- Fetch descriptores ----------
  const fetchDescriptores = async () => {
    if (!token) return;
    await safeFetch(async () => {
      const res = await axios.get(`${API_BASE}/descriptores`, {
        ...authHeader(),
        params: {
          tenant_id: user?.tenant_id || "",
          sede_id: user?.sede_id || "",
        },
      });
      const rows = res?.data || [];
      const filtered = grado ? rows.filter((d) => String(d.grado) === String(grado)) : rows;
      setDescriptores(filtered);
    });
  };

  // ---------- Fetch evaluaciones ----------
  const fetchEvaluaciones = async () => {
    if (!token || !grado || !periodo) return;
    await safeFetch(async () => {
      const res = await axios.get(`${API_BASE}/evaluaciones`, {
        ...authHeader(),
        params: {
          grado,
          periodo_id: periodo,
          tenant_id: user?.tenant_id || "",
          sede_id: user?.sede_id || "",
        },
      });
      const rows = res?.data || [];
      setEvaluaciones(rows);
    });
  };

  // ---------- Initial loads ----------
  useEffect(() => {
    if (!token) return;
    fetchConfig();
    fetchDescriptores();
  }, [token]);

  useEffect(() => {
    if (!token) return;
    fetchDescriptores();
  }, [token, grado]);

  useEffect(() => {
    if (!token) return;
    if (grado && periodo) fetchEvaluaciones();
  }, [token, grado, periodo]);

  // ---------- Handlers ----------
  const handleGuardarConfig = async () => {
    await safeFetch(async () => {
      await axios.post(`${API_BASE}/configuracion`, { ...config, grado, tenant_id: user?.tenant_id, sede_id: user?.sede_id }, authHeader());
      await fetchConfig();
      alert("Configuración guardada");
    });
  };

  const handleCrearDescriptor = async () => {
    if (!nuevoDescriptor.nombre_descriptor?.trim()) return alert("Ingrese nombre descriptor");
    await safeFetch(async () => {
      await axios.post(`${API_BASE}/descriptores`, { ...nuevoDescriptor, grado, tenant_id: user?.tenant_id, sede_id: user?.sede_id }, authHeader());
      setNuevoDescriptor({ nombre_descriptor: "", tipo_valoracion: "numerico", posicion: 1, grado: "" });
      await fetchDescriptores();
    });
  };

  const handleToggleActivo = async (id, activo) => {
    await safeFetch(async () => {
      await axios.patch(`${API_BASE}/descriptores/${id}`, { activo: activo ? 0 : 1, tenant_id: user?.tenant_id, sede_id: user?.sede_id }, authHeader());
      await fetchDescriptores();
    });
  };

  const handleEvalChange = (evalId, field, value) => {
    setEvaluaciones((prev) => prev.map((r) => (r.id === evalId ? { ...r, [field]: value } : r)));
  };

  const handleGuardarEvaluaciones = async () => {
    if (!evaluaciones.length) return alert("No hay evaluaciones para guardar");
    await safeFetch(async () => {
      await Promise.all(
        evaluaciones.map((e) =>
          axios.post(
            `${API_BASE}/evaluaciones`,
            {
              estudiante_id: e.estudiante_id,
              descriptor_id: e.descriptor_id,
              valor: e.valor ?? null,
              desempeño: e.desempeño ?? null,
              observacion: e.observacion ?? null,
              periodo_id: e.periodo_id ?? periodo,
              tenant_id: user?.tenant_id,
              sede_id: user?.sede_id,
            },
            authHeader()
          )
        )
      );
      alert("Evaluaciones guardadas");
      await fetchEvaluaciones();
    });
  };

  // ---------- Render ----------
  const cfg = config || { columnas: 5, mostrar_observaciones: 1, tipo_valoracion: "numerico" };

  return (
    <div style={{ padding: 16 }}>
      <h1>📘 Módulo de Comportamiento</h1>
      {error && <div style={{ color: "crimson", marginBottom: 8 }}>{error}</div>}
      {loading && <div style={{ marginBottom: 8 }}>⏳ Cargando...</div>}

      <div style={{ marginBottom: 12 }}>
        <button onClick={() => setTab("configuracion")} style={{ marginRight: 8 }}>⚙️ Configuración</button>
        <button onClick={() => setTab("descriptores")} style={{ marginRight: 8 }}>📝 Descriptores</button>
        <button onClick={() => setTab("evaluaciones")}>📊 Evaluaciones</button>
      </div>

      {tab === "configuracion" && (
        <div style={{ border: "1px solid #ddd", padding: 12, borderRadius: 6 }}>
          <h2>Configuración</h2>
          <div style={{ marginBottom: 8 }}>
            <label>Grado (opcional)</label><br />
            <input value={grado} onChange={(e) => setGrado(e.target.value)} placeholder="Ej: 10" />
          </div>
          <div style={{ marginBottom: 8 }}>
            <label>Columnas</label><br />
            <input type="number" value={cfg.columnas} onChange={(e) => setConfig((p) => ({ ...p, columnas: Number(e.target.value) }))} />
          </div>
          <div style={{ marginBottom: 8 }}>
            <label>
              <input type="checkbox" checked={Boolean(cfg.mostrar_observaciones)} onChange={(e) => setConfig((p) => ({ ...p, mostrar_observaciones: e.target.checked ? 1 : 0 }))} />
              {" "}Mostrar observaciones
            </label>
          </div>
          <div style={{ marginBottom: 8 }}>
            <label>Tipo de valoración</label><br />
            <select value={cfg.tipo_valoracion} onChange={(e) => setConfig((p) => ({ ...p, tipo_valoracion: e.target.value }))}>
              <option value="numerico">Numérico</option>
              <option value="desempeno">Desempeño</option>
            </select>
          </div>
          <button onClick={handleGuardarConfig}>💾 Guardar configuración</button>
        </div>
      )}

      {tab === "descriptores" && (
        <div style={{ border: "1px solid #ddd", padding: 12, borderRadius: 6 }}>
          <h2>Descriptores</h2>
          <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
            <input placeholder="Nombre descriptor" value={nuevoDescriptor.nombre_descriptor} onChange={(e) => setNuevoDescriptor((p) => ({ ...p, nombre_descriptor: e.target.value }))} />
            <select value={nuevoDescriptor.tipo_valoracion} onChange={(e) => setNuevoDescriptor((p) => ({ ...p, tipo_valoracion: e.target.value }))}>
              <option value="numerico">Numérico</option>
              <option value="desempeno">Desempeño</option>
            </select>
            <button onClick={handleCrearDescriptor}>➕ Agregar</button>
          </div>
          <table border={1} cellPadding={6} style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th>ID</th>
                <th>Nombre</th>
                <th>Tipo</th>
                <th>Grado</th>
                <th>Activo</th>
                <th>Acción</th>
              </tr>
            </thead>
            <tbody>
              {descriptores.map((d) => (
                <tr key={d.id}>
                  <td>{d.id}</td>
                  <td>{d.nombre_descriptor}</td>
                  <td>{d.tipo_valoracion}</td>
                  <td>{d.grado ?? "-"}</td>
                  <td>{d.activo ? "✅" : "❌"}</td>
                  <td>
                    <button onClick={() => handleToggleActivo(d.id, d.activo)}>{d.activo ? "Desactivar" : "Activar"}</button>
                  </td>
                </tr>
              ))}
              {descriptores.length === 0 && <tr><td colSpan={6} style={{ textAlign: "center" }}>No hay descriptores</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {tab === "evaluaciones" && (
        <div style={{ border: "1px solid #ddd", padding: 12, borderRadius: 6 }}>
          <h2>Evaluaciones</h2>
          <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
            <input placeholder="Grado" value={grado} onChange={(e) => setGrado(e.target.value)} />
            <input placeholder="Periodo ID" value={periodo} onChange={(e) => setPeriodo(e.target.value)} />
            <button onClick={fetchEvaluaciones}>🔄 Cargar</button>
          </div>
          <table border={1} cellPadding={6} style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th>Estudiante</th>
                <th>Descriptor</th>
                <th>Tipo</th>
                <th>Valor / Desempeño</th>
                <th>Observación</th>
              </tr>
            </thead>
            <tbody>
              {evaluaciones.map((ev) => (
                <tr key={ev.id}>
                  <td>{ev.nombres_estudiante || ev.apellidos_estudiante ? `${ev.nombres_estudiante || ""} ${ev.apellidos_estudiante || ""}` : ev.estudiante_id}</td>
                  <td>{ev.nombre_descriptor ?? ev.descriptor_id}</td>
                  <td>{ev.tipo_valoracion ?? "-"}</td>
                  <td>
                    {ev.tipo_valoracion === "numerico" ? (
                      <input type="number" value={ev.valor ?? ""} onChange={(e) => handleEvalChange(ev.id, "valor", e.target.value)} />
                    ) : (
                      <select value={ev.desempeño ?? ""} onChange={(e) => handleEvalChange(ev.id, "desempeño", e.target.value)}>
                        <option value="">--</option>
                        <option value="Excelente">Excelente</option>
                        <option value="Bueno">Bueno</option>
                        <option value="Regular">Regular</option>
                        <option value="Necesita Mejorar">Necesita Mejorar</option>
                      </select>
                    )}
                  </td>
                  <td><input value={ev.observacion ?? ""} onChange={(e) => handleEvalChange(ev.id, "observacion", e.target.value)} /></td>
                </tr>
              ))}
              {evaluaciones.length === 0 && <tr><td colSpan={5} style={{ textAlign: "center" }}>No hay evaluaciones cargadas</td></tr>}
            </tbody>
          </table>
          <div style={{ marginTop: 12 }}>
            <button onClick={handleGuardarEvaluaciones}>💾 Guardar evaluaciones</button>
          </div>
        </div>
      )}
    </div>
  );
}
